<template>
    <div :class="['view-container', standalone ? 'view-container--standalone' : '']">
        <div v-if="!standalone && item && item.token" class="mb-4 flex justify-end">
            <a :href="getViewInNewTabUrl()" target="_blank" rel="noopener noreferrer" class="cp-open-tab">
                <font-awesome-icon :icon="['fas', 'external-link-alt']" />
                Open dedicated page
            </a>
        </div>

        <!-- Basic Information: full in modal; supplemental only on dedicated page -->
        <section v-if="showFullBasicInfo || showSupplementalBasicInfo" class="view-section">
            <div class="section-header">
                <div class="section-icon">
                    <font-awesome-icon :icon="['fas', 'building']" class="text-lg" />
                </div>
                <h2 class="section-title">{{ showFullBasicInfo ? 'Basic Information' : 'Additional Details' }}</h2>
            </div>

            <div v-if="showFullBasicInfo" class="info-grid">
                <div class="info-logo-card">
                    <div class="info-logo-wrapper">
                        <img v-if="item.logo" class="info-logo-img" :src="getSrcFile(item.logo)" alt="Partner logo" />
                        <font-awesome-icon v-else :icon="['fas', 'building']" class="info-logo-fallback" />
                    </div>
                </div>
                <div class="info-details">
                    <div class="info-field">
                        <span class="info-label">Name</span>
                        <span class="info-value info-value--strong">{{ item.name }}</span>
                    </div>
                    <div class="info-field-grid">
                        <div class="info-field">
                            <span class="info-label">Location</span>
                            <span class="info-value">{{ item.location || '—' }}</span>
                        </div>
                        <div class="info-field">
                            <span class="info-label">Entity</span>
                            <span class="info-value">
                                <span v-if="item.entity" class="cp-chip">{{ getEntityName(item.entity) }}</span>
                                <span v-else>—</span>
                            </span>
                        </div>
                        <div class="info-field">
                            <span class="info-label">Industry</span>
                            <span class="info-value">{{ industryName }}</span>
                        </div>
                        <div class="info-field">
                            <span class="info-label">Type of Collaboration</span>
                            <span class="info-value">
                                <span v-if="item.type_of_collaboration" class="cp-chip">{{ item.type_of_collaboration }}</span>
                                <span v-else>—</span>
                            </span>
                        </div>
                    </div>
                    <div class="info-field">
                        <span class="info-label">Classifications</span>
                        <div v-if="classificationList.length" class="cp-chip-row">
                            <span v-for="(label, idx) in classificationList" :key="'cls-' + idx" class="cp-chip cp-chip--slate">
                                {{ label }}
                            </span>
                        </div>
                        <span v-else class="info-value">—</span>
                    </div>
                    <div v-if="item.parent_partner" class="info-field">
                        <span class="info-label">Group/Mother Company</span>
                        <span class="info-value">
                            {{ item.parent_partner.name }}
                            <span class="cp-chip cp-chip--muted ml-2">
                                {{ item.parent_partner.is_pipeline ? 'Pipeline' : 'Current' }}
                            </span>
                        </span>
                    </div>
                    <div v-if="item.is_signed === true" class="info-field">
                        <span class="info-label">Signed At</span>
                        <span class="info-value info-value-highlight">{{ getSignedAt }}</span>
                    </div>
                </div>
            </div>

            <div v-else class="info-field-grid">
                <div v-if="item.parent_partner" class="info-field">
                    <span class="info-label">Group/Mother Company</span>
                    <span class="info-value">
                        {{ item.parent_partner.name }}
                        <span class="cp-chip cp-chip--muted ml-2">
                            {{ item.parent_partner.is_pipeline ? 'Pipeline' : 'Current' }}
                        </span>
                    </span>
                </div>
                <div v-if="item.is_signed === true" class="info-field">
                    <span class="info-label">Signed At</span>
                    <span class="info-value info-value-highlight">{{ getSignedAt }}</span>
                </div>
            </div>
        </section>

        <!-- Child Partners -->
        <section v-if="hasChildPartners" class="view-section">
            <div class="section-header">
                <div class="section-icon section-icon-children">
                    <font-awesome-icon :icon="['fas', 'sitemap']" class="text-lg" />
                </div>
                <div>
                    <h2 class="section-title">Child Partners</h2>
                    <p class="section-subtitle">{{ subsidiaryPartners.length }} linked partner{{ subsidiaryPartners.length === 1 ? '' : 's' }}</p>
                </div>
            </div>

            <div class="child-list">
                <div
                    v-for="(child, index) in subsidiaryPartners"
                    :key="child.id"
                    class="child-row"
                >
                    <span class="child-row__num">{{ index + 1 }}</span>
                    <div class="child-row__logo">
                        <img v-if="child.logo" :src="getSrcFile(child.logo)" alt="" />
                        <font-awesome-icon v-else :icon="['fas', 'building']" />
                    </div>
                    <div class="child-row__body">
                        <button type="button" class="cp-name-link" @click="viewChildPartner(child)">
                            {{ child.name }}
                        </button>
                        <div class="cp-chip-row mt-1">
                            <span class="cp-chip cp-chip--muted">{{ child.is_pipeline ? 'Pipeline' : 'Current' }}</span>
                            <span v-if="child.entity" class="cp-chip">{{ getEntityName(child.entity) }}</span>
                            <span v-if="child.type_of_collaboration" class="cp-chip cp-chip--slate">{{ child.type_of_collaboration }}</span>
                        </div>
                    </div>
                    <button type="button" class="cp-action-btn" title="Open" @click="viewChildPartner(child)">
                        <font-awesome-icon :icon="['fas', 'external-link-alt']" />
                    </button>
                </div>
            </div>
        </section>

        <!-- Contact Person -->
        <section class="view-section">
            <div class="section-header">
                <div class="section-icon">
                    <font-awesome-icon :icon="['fas', 'users']" class="text-lg" />
                </div>
                <div>
                    <h2 class="section-title">Contact Person</h2>
                    <p class="section-subtitle">Primary partnership contacts</p>
                </div>
            </div>

            <div v-if="hasContactPerson" class="people-grid">
                <article v-for="(cp, index) in item.contact_person" :key="'cp-' + index" class="person-card">
                    <div class="person-card__avatar">{{ getInitials(cp.name) }}</div>
                    <div class="person-card__body">
                        <h3 class="person-card__name">{{ cp.name || '—' }}</h3>
                        <p class="person-card__role">{{ cp.designation || 'No designation' }}</p>
                        <div class="person-card__links">
                            <a v-if="cp.email" :href="'mailto:' + cp.email" class="person-card__link">
                                <font-awesome-icon :icon="['fas', 'envelope']" />
                                {{ cp.email }}
                            </a>
                            <a v-if="cp.phone" :href="'tel:' + cp.phone" class="person-card__link">
                                <font-awesome-icon :icon="['fas', 'phone']" />
                                {{ cp.phone }}
                            </a>
                        </div>
                    </div>
                </article>
            </div>
            <div v-else class="empty-state">
                <p class="empty-state-text">No contact information available</p>
            </div>
        </section>

        <!-- Joint Committee Members -->
        <section class="view-section">
            <div class="section-header">
                <div class="section-icon section-icon-jcm">
                    <font-awesome-icon :icon="['fas', 'user-tie']" class="text-lg" />
                </div>
                <div class="section-header__text">
                    <h2 class="section-title">Joint Committee Members</h2>
                    <p class="section-subtitle">Internal and external committee representatives</p>
                </div>
                <button
                    v-if="standalone && hasJointCommitteeMembers"
                    type="button"
                    class="jcm-email-trigger"
                    @click="openJointCommitteeEmailCompose"
                >
                    <font-awesome-icon :icon="['fas', 'envelope']" />
                    <span>Email committee</span>
                </button>
            </div>

            <div v-if="hasJointCommitteeMembers" class="people-grid">
                <article
                    v-for="(member, index) in item.joint_committee_members"
                    :key="'jcm-' + index"
                    class="person-card"
                >
                    <div class="person-card__avatar person-card__avatar--jcm">{{ getInitials(member.name) }}</div>
                    <div class="person-card__body">
                        <div class="flex items-center gap-2 flex-wrap mb-1">
                            <h3 class="person-card__name m-0">{{ member.name || '—' }}</h3>
                            <span
                                :class="member.user_id ? 'jcm-type-badge jcm-type-badge--internal' : 'jcm-type-badge jcm-type-badge--external'"
                            >
                                {{ member.user_id ? 'Internal' : 'External' }}
                            </span>
                        </div>
                        <p class="person-card__role">{{ member.title || 'No title' }}</p>
                        <p class="person-card__meta">{{ formatJointCommitteeEntity(member) }}</p>
                        <div class="person-card__links">
                            <a v-if="member.email" :href="'mailto:' + member.email" class="person-card__link">
                                <font-awesome-icon :icon="['fas', 'envelope']" />
                                {{ member.email }}
                            </a>
                            <a v-if="member.mobile" :href="'tel:' + member.mobile" class="person-card__link">
                                <font-awesome-icon :icon="['fas', 'phone']" />
                                {{ member.mobile }}
                            </a>
                        </div>
                    </div>
                </article>
            </div>
            <div v-else class="empty-state">
                <p class="empty-state-text">No joint committee members listed</p>
            </div>

            <div v-if="standalone && item.token" class="jcm-email-history-wrap">
                <div class="jcm-email-history-wrap__head">
                    <h3 class="jcm-email-history-wrap__title">Email activity</h3>
                    <p class="jcm-email-history-wrap__hint">Tracked subject, content, and delivery status</p>
                </div>
                <joint-committee-email-history
                    :partner-token="item.token"
                    :refresh-key="jointCommitteeEmailRefreshKey"
                />
            </div>
        </section>

        <!-- Account Managers -->
        <section class="view-section">
            <div class="section-header">
                <div class="section-icon section-icon-am">
                    <font-awesome-icon :icon="['fas', 'user-check']" class="text-lg" />
                </div>
                <div>
                    <h2 class="section-title">Account Managers</h2>
                    <p class="section-subtitle">Internal owners for this partnership</p>
                </div>
            </div>

            <div v-if="hasAccountManagers" class="people-grid">
                <article
                    v-for="(manager, index) in item.account_managers"
                    :key="'am-' + index"
                    class="person-card"
                >
                    <div class="person-card__avatar person-card__avatar--am">{{ getInitials(manager.name) }}</div>
                    <div class="person-card__body">
                        <h3 class="person-card__name">{{ manager.name || '—' }}</h3>
                        <p class="person-card__role">{{ manager.title || 'No title' }}</p>
                        <p class="person-card__meta">{{ formatAccountManagerEntity(manager) }}</p>
                        <div class="person-card__links">
                            <a v-if="manager.email" :href="'mailto:' + manager.email" class="person-card__link">
                                <font-awesome-icon :icon="['fas', 'envelope']" />
                                {{ manager.email }}
                            </a>
                            <a v-if="manager.mobile" :href="'tel:' + manager.mobile" class="person-card__link">
                                <font-awesome-icon :icon="['fas', 'phone']" />
                                {{ manager.mobile }}
                            </a>
                        </div>
                    </div>
                </article>
            </div>
            <div v-else class="empty-state">
                <p class="empty-state-text">No account managers listed</p>
            </div>
        </section>

        <!-- Uploaded Documents -->
        <section class="view-section">
            <div class="section-header">
                <div class="section-icon">
                    <font-awesome-icon :icon="['fas', 'paperclip']" class="text-lg" />
                </div>
                <div>
                    <h2 class="section-title">Uploaded Documents</h2>
                    <p class="section-subtitle">Files attached to this partnership</p>
                </div>
            </div>

            <div v-if="hasUploadedDocuments" class="file-list">
                <div
                    v-for="(attachment, index) in uploadedDocuments"
                    :key="'doc-' + index"
                    class="file-row"
                >
                    <div :class="['file-row__icon', fileIconClass(attachment)]">
                        <font-awesome-icon :icon="fileIcon(attachment)" />
                    </div>
                    <div class="file-row__body">
                        <p class="file-row__title">{{ attachment.file_name || attachment.original_filename || 'Document' }}</p>
                        <p class="file-row__meta">
                            {{ attachment.original_filename || '—' }}
                            <span v-if="attachment.file_extension" class="cp-chip cp-chip--muted ml-2">
                                {{ String(attachment.file_extension).toUpperCase() }}
                            </span>
                        </p>
                    </div>
                    <div class="file-row__actions">
                        <button type="button" class="cp-action-btn" title="View" @click="openDocumentPreview(attachment)">
                            <icon-eye class="w-4 h-4"></icon-eye>
                        </button>
                        <button type="button" class="cp-action-btn" title="Download" @click="downloadAttachment(attachment)">
                            <font-awesome-icon :icon="['fas', 'download']" />
                        </button>
                    </div>
                </div>
            </div>
            <div v-else class="empty-state">
                <p class="empty-state-text">No documents uploaded</p>
            </div>
        </section>

        <!-- Management Approval Documents -->
        <section v-if="isShowManagementApprovalDocuments" class="view-section">
            <div class="section-header">
                <div class="section-icon section-icon-approval">
                    <font-awesome-icon :icon="['fas', 'stamp']" class="text-lg" />
                </div>
                <div>
                    <h2 class="section-title">Management Approval Documents</h2>
                    <p class="section-subtitle">Approval-related attachments</p>
                </div>
            </div>

            <div v-if="hasManagementApprovalDocuments" class="file-list">
                <div
                    v-for="(attachment, index) in managementApprovalDocuments"
                    :key="'mad-' + index"
                    class="file-row"
                >
                    <div :class="['file-row__icon', fileIconClass(attachment)]">
                        <font-awesome-icon :icon="fileIcon(attachment)" />
                    </div>
                    <div class="file-row__body">
                        <p class="file-row__title">{{ attachment.file_name || attachment.original_filename || 'Document' }}</p>
                        <p class="file-row__meta">
                            {{ attachment.original_filename || '—' }}
                            <span v-if="attachment.file_extension" class="cp-chip cp-chip--muted ml-2">
                                {{ String(attachment.file_extension).toUpperCase() }}
                            </span>
                        </p>
                    </div>
                    <div class="file-row__actions">
                        <button type="button" class="cp-action-btn" title="View" @click="openDocumentPreview(attachment)">
                            <icon-eye class="w-4 h-4"></icon-eye>
                        </button>
                        <button type="button" class="cp-action-btn" title="Download" @click="downloadAttachment(attachment)">
                            <font-awesome-icon :icon="['fas', 'download']" />
                        </button>
                    </div>
                </div>
            </div>
            <div v-else class="empty-state">
                <p class="empty-state-text">No documents uploaded</p>
            </div>
        </section>

        <!-- MoU Effectiveness Cards -->
        <section class="view-section">
            <div class="section-header">
                <div class="section-icon section-icon-mou">
                    <font-awesome-icon :icon="['fas', 'clipboard-check']" class="text-lg" />
                </div>
                <div>
                    <h2 class="section-title">MoU Effectiveness Cards</h2>
                    <p class="section-subtitle">Tracked MoU effectiveness records</p>
                </div>
            </div>

            <div v-if="hasMouEffectivenessCard" class="data-table-wrapper">
                <table class="data-table">
                    <thead>
                        <tr>
                            <th class="data-table-th data-table-th-num">#</th>
                            <th class="data-table-th">First Party</th>
                            <th class="data-table-th">Second Party</th>
                            <th class="data-table-th">Created At</th>
                            <th class="data-table-th">Last Updated</th>
                            <th class="data-table-th data-table-th-action">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(mou, index) in mouEffectivenessCards" :key="'mou-' + index" class="data-table-tr">
                            <td class="data-table-td data-table-td-num">{{ index + 1 }}</td>
                            <td class="data-table-td">{{ (mou.first_party_labels || []).join(', ') }}</td>
                            <td class="data-table-td">{{ mou.second_party }}</td>
                            <td class="data-table-td">{{ getFormattedDate(mou.created_at) }}</td>
                            <td class="data-table-td">{{ getFormattedDate(mou.updated_at || mou.created_at) }}</td>
                            <td class="data-table-td data-table-td-action">
                                <button
                                    type="button"
                                    class="cp-action-btn"
                                    title="View"
                                    @click.stop="viewMouEffectivenessCard(mou.id, item.name)"
                                >
                                    <icon-eye class="w-4 h-4"></icon-eye>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div v-else class="empty-state">
                <p class="empty-state-text">No MoU Effectiveness Card</p>
            </div>
        </section>

        <base-modal
            v-if="showDocumentPreviewModal"
            :title="previewModalTitle"
            @close="closeDocumentPreview"
            width="90vw"
            height="85vh"
        >
            <div class="document-preview-container">
                <div v-if="previewAttachment" class="document-preview-content">
                    <div v-if="isPdfFile(previewAttachment)" class="document-preview-iframe">
                        <iframe :src="getSrcFile(previewAttachment.path)" class="document-iframe" frameborder="0"></iframe>
                    </div>
                    <div v-else-if="isImageFile(previewAttachment)" class="document-preview-image">
                        <img
                            :src="getSrcFile(previewAttachment.path)"
                            :alt="previewAttachment.file_name"
                            class="document-img"
                        />
                    </div>
                    <div v-else class="document-preview-fallback">
                        <font-awesome-icon :icon="['fas', 'file-alt']" class="document-fallback-icon" />
                        <p class="document-fallback-text">Preview not available for this file type.</p>
                        <p class="document-fallback-hint">Please download to view the content.</p>
                    </div>
                </div>
                <div v-if="previewAttachment" class="document-preview-footer">
                    <button type="button" class="document-download-btn" @click="downloadAttachment(previewAttachment)">
                        <font-awesome-icon :icon="['fas', 'download']" class="mr-2" />
                        Download
                    </button>
                </div>
            </div>
        </base-modal>

        <transition name="fade" mode="out-in">
            <base-full-modal
                v-if="isViewMouEffectivenessCard"
                :title="'MoU Effectiveness Card - ' + selectedTitle"
                @close="closeMouEffectivenessCard"
            >
                <mou-effectiveness-card
                    :current-partner-id="item.id"
                    :mou-id="mouId"
                    from="current-partners-view"
                    @close="closeMouEffectivenessCard"
                ></mou-effectiveness-card>
            </base-full-modal>
        </transition>

        <joint-committee-email-compose
            v-if="standalone && item.token"
            :visible="showJointCommitteeEmailCompose"
            :partner-token="item.token"
            :partner-name="item.name"
            :members="item.joint_committee_members || []"
            :sender-name="currentUserName"
            @close="closeJointCommitteeEmailCompose"
            @sent="onJointCommitteeEmailSent"
        />
    </div>
</template>

<script>
import gql from "graphql-tag";
import IconEye from "../../components/icons/IconEye.vue";
import BaseModal from "../../components/ui/BaseModal.vue";
import { formattedDate, formattedDateWithTimeAmPm } from "../../helpers/utils";
import MouEffectivenessCard from "./MouEffectivenessCard/index.vue";
import JointCommitteeEmailCompose from "./JointCommitteeEmailCompose.vue";
import JointCommitteeEmailHistory from "./JointCommitteeEmailHistory.vue";

export default {
    name: "current-partners-view",
    components: {
        IconEye,
        BaseModal,
        MouEffectivenessCard,
        JointCommitteeEmailCompose,
        JointCommitteeEmailHistory,
    },
    props: {
        item: { type: Object, required: true },
        standalone: { type: Boolean, default: false },
    },
    data() {
        return {
            isViewMouEffectivenessCard: false,
            mouId: null,
            selectedTitle: "",
            showDocumentPreviewModal: false,
            previewAttachment: null,
            showJointCommitteeEmailCompose: false,
            jointCommitteeEmailRefreshKey: 0,
        };
    },
    computed: {
        currentUserName() {
            return (this.$store && this.$store.getters && this.$store.getters.name)
                ? this.$store.getters.name
                : "";
        },
        showFullBasicInfo() {
            return !this.standalone;
        },
        showSupplementalBasicInfo() {
            if (!this.standalone) {
                return false;
            }
            return !!(this.item.parent_partner || this.item.is_signed === true);
        },
        industryName() {
            if (this.item && this.item.industry_relation && this.item.industry_relation.name) {
                return this.item.industry_relation.name;
            }
            return "—";
        },
        classificationList() {
            if (!this.item || !this.item.classifications || !this.item.classifications.length) {
                return [];
            }
            return this.item.classifications;
        },
        hasContactPerson() {
            return this.item.contact_person && this.item.contact_person.length > 0;
        },
        hasJointCommitteeMembers() {
            return this.item.joint_committee_members && this.item.joint_committee_members.length > 0;
        },
        hasAccountManagers() {
            return this.item.account_managers && this.item.account_managers.length > 0;
        },
        hasUploadedDocuments() {
            return Array.isArray(this.item.attachments) &&
                this.item.attachments.some(function (att) {
                    return att.uploaded_from === "Upload Documents";
                });
        },
        uploadedDocuments() {
            if (!this.hasUploadedDocuments) {
                return [];
            }
            return this.item.attachments.filter(function (att) {
                return att.uploaded_from === "Upload Documents";
            });
        },
        hasManagementApprovalDocuments() {
            return Array.isArray(this.item.attachments) &&
                this.item.attachments.some(function (att) {
                    return att.uploaded_from === "Management Approval";
                });
        },
        managementApprovalDocuments() {
            if (!this.hasManagementApprovalDocuments) {
                return [];
            }
            return this.item.attachments.filter(function (att) {
                return att.uploaded_from === "Management Approval";
            });
        },
        getSignedAt() {
            return formattedDate(this.item.approved_at);
        },
        isShowManagementApprovalDocuments() {
            var is_bot_approval = this.item.is_bot_approval;
            var is_md_approval = this.item.is_md_approval;
            var is_line_manager_approval = this.item.is_line_manager_approval;
            return (
                is_bot_approval !== null ||
                is_md_approval !== null ||
                is_line_manager_approval !== null
            );
        },
        hasMouEffectivenessCard() {
            return this.item.mou_effectiveness_cards && this.item.mou_effectiveness_cards.length > 0;
        },
        mouEffectivenessCards() {
            if (this.hasMouEffectivenessCard) {
                return this.item.mou_effectiveness_cards;
            }
            return [];
        },
        previewModalTitle() {
            return this.previewAttachment
                ? "Document: " + (this.previewAttachment.file_name || this.previewAttachment.original_filename)
                : "Document Preview";
        },
        hasChildPartners() {
            return this.item.subsidiary_partners && this.item.subsidiary_partners.length > 0;
        },
        subsidiaryPartners() {
            return this.item.subsidiary_partners || [];
        },
    },
    methods: {
        getSrcFile(url) {
            return process.env.VUE_APP_PUBLIC_URL + url;
        },
        getViewInNewTabUrl() {
            var route = this.$router.resolve({
                name: "Current Partner View",
                params: { token: this.item.token },
            });
            return route.href;
        },
        getEntityName(entity) {
            if (!entity) {
                return "";
            }
            return String(entity).replace(/ATHS/g, "ATS");
        },
        getInitials(name) {
            if (!name) {
                return "?";
            }
            var parts = String(name).trim().split(/\s+/);
            if (parts.length === 1) {
                return parts[0].charAt(0).toUpperCase();
            }
            return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
        },
        formatJointCommitteeEntity(member) {
            if (!member) {
                return "—";
            }
            if (member.display_entity) {
                return member.display_entity;
            }
            if (member.entity) {
                return String(member.entity).replace(/ATHS/g, "ATS");
            }
            return "—";
        },
        formatAccountManagerEntity(manager) {
            if (!manager) {
                return "—";
            }
            if (manager.display_entity) {
                return manager.display_entity;
            }
            if (manager.entity) {
                return String(manager.entity).replace(/ATHS/g, "ATS");
            }
            return "—";
        },
        getFormattedDate(date) {
            return formattedDateWithTimeAmPm(date);
        },
        fileIcon(attachment) {
            if (this.isPdfFile(attachment)) {
                return ["fas", "file-pdf"];
            }
            if (this.isImageFile(attachment)) {
                return ["fas", "file-image"];
            }
            return ["fas", "file-alt"];
        },
        fileIconClass(attachment) {
            if (this.isPdfFile(attachment)) {
                return "file-row__icon--pdf";
            }
            if (this.isImageFile(attachment)) {
                return "file-row__icon--image";
            }
            return "file-row__icon--file";
        },
        viewChildPartner(child) {
            if (!child || !child.token) {
                return;
            }
            var routeName = child.is_pipeline ? "Pipeline Partner View" : "Current Partner View";
            var route = this.$router.resolve({
                name: routeName,
                params: { token: child.token },
            });
            window.open(route.href, "_blank");
        },
        viewMouEffectivenessCard(mouId, name) {
            this.mouId = mouId;
            this.selectedTitle = name;
            this.isViewMouEffectivenessCard = true;
        },
        closeMouEffectivenessCard() {
            this.isViewMouEffectivenessCard = false;
            this.mouId = null;
            this.selectedTitle = "";
        },
        openDocumentPreview(attachment) {
            this.previewAttachment = attachment;
            this.showDocumentPreviewModal = true;
        },
        closeDocumentPreview() {
            this.showDocumentPreviewModal = false;
            this.previewAttachment = null;
        },
        openJointCommitteeEmailCompose() {
            this.showJointCommitteeEmailCompose = true;
        },
        closeJointCommitteeEmailCompose() {
            this.showJointCommitteeEmailCompose = false;
        },
        onJointCommitteeEmailSent() {
            this.jointCommitteeEmailRefreshKey = this.jointCommitteeEmailRefreshKey + 1;
        },
        isPdfFile(attachment) {
            if (!attachment || !attachment.file_extension) {
                return false;
            }
            return String(attachment.file_extension).toLowerCase() === "pdf";
        },
        isImageFile(attachment) {
            if (!attachment || !attachment.file_extension) {
                return false;
            }
            var ext = String(attachment.file_extension).toLowerCase();
            return ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].indexOf(ext) >= 0;
        },
        async forceDownload(attachmentId, filename) {
            try {
                var response = await this.$apollo.mutate({
                    mutation: gql`
                        mutation GetCurrentPartnerAttachmentContent($id: ID!) {
                            getCurrentPartnerAttachmentContent(id: $id) {
                                error
                                error_code
                                message
                                data {
                                    content
                                    file_type
                                }
                            }
                        }
                    `,
                    variables: {
                        id: attachmentId,
                    },
                });

                var payload = response.data && response.data.getCurrentPartnerAttachmentContent
                    ? response.data.getCurrentPartnerAttachmentContent
                    : null;
                if (!payload || payload.error) {
                    throw new Error((payload && payload.message) || "Download failed");
                }
                if (!payload.data || !payload.data.content) {
                    throw new Error("Download failed");
                }

                var base64Content = payload.data.content;
                var fileType = payload.data.file_type;
                var binaryString = atob(base64Content);
                var bytes = new Uint8Array(binaryString.length);
                for (var i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }

                var blob = new Blob([bytes], { type: fileType });
                var blobUrl = window.URL.createObjectURL(blob);
                var link = document.createElement("a");
                link.href = blobUrl;
                link.download = filename;
                link.style.display = "none";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
            } catch (error) {
                console.error("Error downloading file:", error);
                if (this.$swal) {
                    this.$swal.fire({
                        icon: "error",
                        title: "Download Failed",
                        text: "Failed to download the file. Please try again.",
                    });
                }
            }
        },
        async downloadAttachment(attachment) {
            try {
                await this.forceDownload(attachment.id, attachment.original_filename);
            } catch (error) {
                console.error("Error downloading attachment:", error);
                if (this.$swal) {
                    this.$swal.fire({
                        icon: "error",
                        title: "Download Failed",
                        text: "Failed to download the file. Please try again.",
                    });
                }
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.view-container {
    max-width: 56rem;
    margin: 0 auto;
    padding: 1.5rem 1.75rem;
}
.view-container--standalone {
    max-width: none;
    margin: 0;
    padding: 0;
}

.view-section {
    margin-bottom: 1.25rem;
    padding: 1.25rem 1.5rem;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 1rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.view-container--standalone .view-section {
    background: #f8fafc;
    border-color: #e2e8f0;
}
.view-container--standalone .view-section:last-child {
    margin-bottom: 0;
}

.section-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.25rem;
    padding-bottom: 0.875rem;
    border-bottom: 1px solid #e2e8f0;
}
.section-header__text {
    min-width: 0;
    flex: 1;
}
.jcm-email-trigger {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    margin-left: auto;
    padding: 0.5rem 0.875rem;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #fff;
    background: linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%);
    border: none;
    border-radius: 0.5rem;
    cursor: pointer;
    flex-shrink: 0;
}
.jcm-email-trigger:hover {
    box-shadow: 0 4px 12px -2px rgba(37, 99, 235, 0.45);
}
.jcm-email-history-wrap {
    margin-top: 1.25rem;
    padding-top: 1.25rem;
    border-top: 1px solid #e2e8f0;
}
.jcm-email-history-wrap__head {
    margin-bottom: 0.75rem;
}
.jcm-email-history-wrap__title {
    margin: 0;
    font-size: 0.9375rem;
    font-weight: 700;
    color: #111827;
}
.jcm-email-history-wrap__hint {
    margin: 0.125rem 0 0;
    font-size: 0.75rem;
    color: #94a3b8;
}
.section-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 0.75rem;
    background: #dbeafe;
    color: #2563eb;
    flex-shrink: 0;
}
.section-icon-approval {
    background: #d1fae5;
    color: #059669;
}
.section-icon-mou {
    background: #ede9fe;
    color: #7c3aed;
}
.section-icon-children {
    background: #fef3c7;
    color: #d97706;
}
.section-icon-jcm {
    background: #eef2ff;
    color: #4338ca;
}
.section-icon-am {
    background: #e0f2fe;
    color: #0c4a6e;
}
.section-title {
    font-size: 1.0625rem;
    font-weight: 700;
    color: #111827;
    letter-spacing: -0.01em;
    margin: 0;
}
.section-subtitle {
    margin: 0.125rem 0 0;
    font-size: 0.75rem;
    color: #94a3b8;
    font-weight: 500;
}

.cp-open-tab {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.875rem;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #1d4ed8;
    background: #eff6ff;
    border: 1px solid #dbeafe;
    border-radius: 0.5rem;
    text-decoration: none;
}
.cp-open-tab:hover {
    background: #dbeafe;
    color: #1e3a8a;
}

.info-grid {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}
@media (min-width: 640px) {
    .info-grid {
        flex-direction: row;
        align-items: flex-start;
    }
}
.info-logo-wrapper {
    width: 7.5rem;
    height: 7.5rem;
    padding: 0.5rem;
    border-radius: 0.875rem;
    border: 1px solid #e2e8f0;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
}
.info-logo-img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}
.info-logo-fallback {
    font-size: 1.75rem;
    color: #94a3b8;
}
.info-details {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1rem;
}
.info-field-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.875rem 1.25rem;
}
@media (min-width: 640px) {
    .info-field-grid {
        grid-template-columns: 1fr 1fr;
    }
}
.info-field {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}
.info-label {
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #94a3b8;
}
.info-value {
    font-size: 0.875rem;
    color: #1e293b;
    font-weight: 500;
}
.info-value--strong {
    font-size: 1.125rem;
    font-weight: 800;
    color: #111827;
}
.info-value-highlight {
    color: #059669;
    font-weight: 600;
}

.cp-chip-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
}
.cp-chip {
    display: inline-flex;
    align-items: center;
    padding: 0.2rem 0.55rem;
    border-radius: 0.375rem;
    font-size: 0.75rem;
    font-weight: 600;
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #dbeafe;
}
.cp-chip--slate {
    background: #f1f5f9;
    color: #475569;
    border-color: #e2e8f0;
}
.cp-chip--muted {
    background: #f8fafc;
    color: #64748b;
    border-color: #e2e8f0;
}

.cp-name-link {
    padding: 0;
    margin: 0;
    border: none;
    background: transparent;
    font: inherit;
    font-weight: 700;
    color: #1e3a8a;
    cursor: pointer;
    text-align: left;
}
.cp-name-link:hover {
    color: #2563eb;
    text-decoration: underline;
}

.cp-action-btn {
    width: 2rem;
    height: 2rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.5rem;
    border: none;
    background: #f1f5f9;
    color: #475569;
    cursor: pointer;
    font-size: 0.8125rem;
    transition: all 0.15s;
    flex-shrink: 0;
}
.cp-action-btn:hover {
    background: #e0e7ff;
    color: #4338ca;
}

.people-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.875rem;
}
@media (min-width: 768px) {
    .people-grid {
        grid-template-columns: 1fr 1fr;
    }
}
.person-card {
    display: flex;
    gap: 0.875rem;
    padding: 1rem;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 0.875rem;
}
.person-card__avatar {
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 0.75rem;
    background: #dbeafe;
    color: #1d4ed8;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8125rem;
    font-weight: 800;
    flex-shrink: 0;
}
.person-card__avatar--jcm {
    background: #eef2ff;
    color: #4338ca;
}
.person-card__avatar--am {
    background: #e0f2fe;
    color: #0c4a6e;
}
.person-card__body {
    min-width: 0;
    flex: 1;
}
.person-card__name {
    margin: 0 0 0.125rem;
    font-size: 0.9375rem;
    font-weight: 700;
    color: #111827;
}
.person-card__role {
    margin: 0;
    font-size: 0.8125rem;
    color: #64748b;
}
.person-card__meta {
    margin: 0.25rem 0 0;
    font-size: 0.75rem;
    font-weight: 600;
    color: #94a3b8;
}
.person-card__links {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-top: 0.625rem;
}
.person-card__link {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.75rem;
    font-weight: 500;
    color: #2563eb;
    text-decoration: none;
    word-break: break-all;
}
.person-card__link:hover {
    text-decoration: underline;
}

.child-list,
.file-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}
.child-row,
.file-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 0.875rem;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 0.75rem;
}
.child-row__num {
    width: 1.5rem;
    text-align: center;
    font-size: 0.75rem;
    font-weight: 700;
    color: #94a3b8;
    flex-shrink: 0;
}
.child-row__logo {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 0.5rem;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    color: #94a3b8;
    flex-shrink: 0;
}
.child-row__logo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}
.child-row__body {
    min-width: 0;
    flex: 1;
}

.file-row__icon {
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 0.9375rem;
}
.file-row__icon--pdf {
    background: #fee2e2;
    color: #dc2626;
}
.file-row__icon--image {
    background: #dbeafe;
    color: #2563eb;
}
.file-row__icon--file {
    background: #f1f5f9;
    color: #64748b;
}
.file-row__body {
    min-width: 0;
    flex: 1;
}
.file-row__title {
    margin: 0;
    font-size: 0.875rem;
    font-weight: 700;
    color: #111827;
}
.file-row__meta {
    margin: 0.25rem 0 0;
    font-size: 0.75rem;
    color: #64748b;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
}
.file-row__actions {
    display: flex;
    gap: 0.375rem;
    flex-shrink: 0;
}

.data-table-wrapper {
    overflow-x: auto;
    border-radius: 0.75rem;
    border: 1px solid #e2e8f0;
    background: #fff;
}
.data-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.8125rem;
}
.data-table-th {
    padding: 0.75rem 1.25rem;
    text-align: left;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #64748b;
    background: #f8fafc;
    border-bottom: 2px solid #e2e8f0;
}
.data-table-th-num {
    width: 3rem;
    text-align: center;
}
.data-table-th-action {
    text-align: center;
    width: 5rem;
}
.data-table-tr:hover {
    background: #f8fafc;
}
.data-table-td {
    padding: 0.875rem 1.25rem;
    color: #334155;
    border-bottom: 1px solid #f1f5f9;
}
.data-table-td-num {
    text-align: center;
    font-weight: 500;
    color: #64748b;
}
.data-table-td-action {
    text-align: center;
}

.empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.25rem 1rem;
    background: #fff;
    border-radius: 0.75rem;
    border: 1px dashed #cbd5e1;
}
.empty-state-text {
    margin: 0;
    font-size: 0.8125rem;
    color: #94a3b8;
    font-weight: 500;
}

.jcm-type-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.15rem 0.5rem;
    border-radius: 0.375rem;
    font-size: 0.6875rem;
    font-weight: 700;
}
.jcm-type-badge--internal {
    background: #e0e7ff;
    color: #3730a3;
}
.jcm-type-badge--external {
    background: #f1f5f9;
    color: #475569;
}

.ml-2 { margin-left: 0.5rem; }
.mt-1 { margin-top: 0.25rem; }
.mb-1 { margin-bottom: 0.25rem; }
.m-0 { margin: 0; }

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.document-preview-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 400px;
}
.document-preview-content {
    flex: 1;
    overflow: auto;
    min-height: 0;
}
.document-preview-iframe,
.document-preview-image {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: flex-start;
}
.document-iframe {
    width: 100%;
    height: 70vh;
    min-height: 400px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
}
.document-img {
    max-width: 100%;
    max-height: 70vh;
    object-fit: contain;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
}
.document-preview-fallback {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 2rem;
}
.document-fallback-icon {
    font-size: 4rem;
    color: #94a3b8;
    margin-bottom: 1rem;
}
.document-fallback-text {
    font-size: 1rem;
    font-weight: 500;
    color: #334155;
    margin-bottom: 0.5rem;
}
.document-fallback-hint {
    font-size: 0.875rem;
    color: #64748b;
}
.document-preview-footer {
    flex-shrink: 0;
    padding-top: 1rem;
    border-top: 1px solid #e2e8f0;
    display: flex;
    justify-content: center;
}
.document-download-btn {
    display: inline-flex;
    align-items: center;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: #fff;
    background: linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%);
    border: none;
    border-radius: 0.5rem;
    cursor: pointer;
}
.document-download-btn:hover {
    box-shadow: 0 4px 12px -2px rgba(37, 99, 235, 0.45);
    color: #fff;
}
</style>
